
import React, { useState } from 'react';
import { Product, Variant } from '../types';
import { Sparkles, Star, ShoppingCart } from 'lucide-react';
import { formatPrice } from '../constants';

interface ProductCardProps {
  product: Product;
  onAddToCart: (p: Product, v?: Variant) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const currentStock = Number(product.stock) || 0;
  const isOutOfStock = currentStock <= 0;
  const isPromo = product.originalPrice && product.originalPrice > product.price;

  const getBadgeConfig = (label: string) => {
    if (isOutOfStock) return { style: 'bg-brand-offwhite text-brand-black/50', text: 'ÉPUISÉ' };
    
    const l = label?.toLowerCase() || '';
    switch (l) {
      case 'best-seller':
        return { style: 'bg-brand-black text-brand-gold border border-brand-gold/20', icon: <Star size={8} fill="currentColor" />, text: 'BEST-SELLER' };
      case 'nouveau':
        return { style: 'bg-brand-offwhite text-brand-black border border-brand-black/10', icon: <Sparkles size={8} />, text: 'NOUVEAU' };
      case 'promo':
        return { style: 'bg-brand-gold text-white', text: 'PROMO' };
      case 'édition limitée':
        return { style: 'bg-brand-black text-white italic', text: 'ÉDITION LIMITÉE' };
      default:
        return label ? { style: 'bg-brand-black text-brand-offwhite', text: label.toUpperCase() } : null;
    }
  };

  const badge = getBadgeConfig(product.label || (isPromo ? 'promo' : ''));

  return (
    <div 
      className="group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-brand-cream mb-6 shadow-sm border border-brand-black/[0.05] luxury-transition group-hover:shadow-xl group-hover:translate-y-[-4px]">
        <img 
          src={product.image} 
          alt={product.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1.5s] ease-out ${isHovered ? 'scale-110 opacity-0' : 'scale-100'}`}
        />
        
        {product.hoverImage && (
          <img 
            src={product.hoverImage} 
            alt={`${product.name} detail`}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1.5s] ease-out ${isHovered ? 'scale-105 opacity-100' : 'scale-110 opacity-0'}`}
          />
        )}

        {/* Badge dynamique */}
        <div className="absolute top-4 left-4 z-10">
          {badge && (
            <span className={`text-[8px] uppercase tracking-widest px-4 py-2 font-bold flex items-center gap-2 rounded-full shadow-sm ${badge.style}`}>
              {badge.icon}
              {badge.text}
            </span>
          )}
        </div>

        {!isOutOfStock && (
          <div className={`absolute inset-x-0 bottom-6 flex justify-center px-6 transition-all duration-700 transform ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <button 
              onClick={(e) => { e.preventDefault(); onAddToCart(product); }}
              className="w-full bg-brand-offwhite/95 backdrop-blur-md text-brand-black py-4 rounded-full text-[9px] uppercase tracking-widest font-bold shadow-lg hover:bg-brand-gold hover:text-white transition-all flex items-center justify-center gap-2 border border-brand-black/5"
            >
              <ShoppingCart size={14} /> Ajouter au Rituel
            </button>
          </div>
        )}
      </div>

      <div className="text-center px-4">
        <p className="text-[8px] uppercase tracking-[0.3em] text-brand-gold mb-2 font-bold opacity-80">
          {product.category}
        </p>
        <h3 className="font-serif text-xl mb-2 text-brand-black group-hover:text-brand-gold luxury-transition">
          {product.name}
        </h3>
        <div className="flex items-center justify-center gap-4">
          {isPromo && (
            <span className="text-[10px] text-brand-black/40 line-through font-light">
              {formatPrice(product.originalPrice!)}
            </span>
          )}
          <p className={`font-bold tracking-tight text-sm ${isOutOfStock ? 'text-brand-black/30' : (isPromo ? 'text-brand-gold' : 'text-brand-black')}`}>
            {isOutOfStock ? 'Bientôt disponible' : formatPrice(product.price)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
